// @flow
import React, { useState, useEffect } from "react";
import { TextInput, StyleSheet, Text, Keyboard, Animated } from "react-native";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import theme from "app/Theme/Theme";
import EyeClose from "mobile/src/components/Icons/Password/EyeClose";
import EyeOpen from "mobile/src/components/Icons/Password/EyeOpen";
import { PressableOpacity } from "mobile/src/components/common/PressableOpacity";
import normalize from "app/utils/normalize";
import { Box } from "mobile/src/components/common/Box";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";

const ORIGINAL_VALUE = 0;
const SUCCESS_VALUE = 1;

type inputType = {
  value: string;
  testID: string;
  autoCapitalize: string;
  containerPropStyle: any;
  inputPropStyle: any;
  secureTextEntry?: boolean;
  initialBorderColor: string;
  keyboardType?: string;
  placeholder: string;
  error: string;
  returnKeyType: string;
  autoFocus?: boolean;
  passwordType?: boolean;
  placeholderTextColor?: string;
  selectionColor: string;
  onShowPassword: () => mixed;
  onChangeText: (text: string) => mixed;
  onSubmitEditing: () => mixed;
  refInput: Ref;
  endBorderColor: string;
  initialBorderWidth: number;
  endBorderWidth: number;
  textContentType: string;
};

const RoundInput = ({
  value,
  testID,
  autoCapitalize,
  onChangeText,
  placeholder,
  containerPropStyle,
  inputPropStyle,
  autoFocus,
  placeholderTextColor = theme.colors["neutral.600"],
  selectionColor = theme.colors["brand.500"],
  error,
  returnKeyType = "done",
  keyboardType = "default",
  secureTextEntry = false,
  initialBorderColor = theme.colors["neutral.white"],
  endBorderColor = theme.colors["brand.500"],
  initialBorderWidth = 0,
  endBorderWidth = 2,
  textContentType = "none",
  onSubmitEditing = () => Keyboard.dismiss(),
  refInput = null,
}: inputType) => {
  const [isSecure, setSecure] = useState(secureTextEntry);
  const [isFocused, setFocus] = useState(false);

  const borderValue = new Animated.Value(0);
  const borderColorValue = new Animated.Value(0);

  useEffect(() => {
    Animated.timing(borderValue, {
      toValue: isFocused ? SUCCESS_VALUE : ORIGINAL_VALUE,
      duration: 300,
      useNativeDriver: false,
    }).start();
    Animated.timing(borderColorValue, {
      toValue: isFocused ? SUCCESS_VALUE : ORIGINAL_VALUE,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isFocused]);

  let borderWidth = borderValue.interpolate({
    inputRange: [ORIGINAL_VALUE, SUCCESS_VALUE],
    outputRange: [initialBorderWidth, endBorderWidth],
  });

  let borderColor = borderColorValue.interpolate({
    inputRange: [ORIGINAL_VALUE, SUCCESS_VALUE],
    outputRange: [initialBorderColor, endBorderColor],
  });

  return (
    <Box alignItems="center" justifyContent="center" flexDirection="column">
      <Animated.View
        style={[
          styles.containerStyle,
          containerPropStyle,
          { borderColor },
          { borderWidth },
        ]}
      >
        <TextInput
          textAlign="left"
          ref={refInput}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          selectionColor={selectionColor}
          testID={testID}
          style={[
            isWebDesktop ? styles.inputStyleWebDesktop : styles.inputStyle,
            inputPropStyle,
          ]}
          placeholderTextColor={placeholderTextColor}
          keyboardType={keyboardType}
          placeholder={placeholder}
          autoCapitalize={autoCapitalize}
          secureTextEntry={isSecure}
          textContentType={textContentType}
          value={value}
          onChangeText={onChangeText}
          autoFocus={autoFocus}
          underlineColorAndroid="transparent"
          returnKeyType={returnKeyType}
          blurOnSubmit={false}
          onSubmitEditing={onSubmitEditing}
        />
        {secureTextEntry && value !== "" && (
          <PressableOpacity
            style={styles.icon}
            onPress={() => setSecure(!isSecure)}
          >
            {isSecure ? <EyeClose /> : <EyeOpen />}
          </PressableOpacity>
        )}
      </Animated.View>
      {error ? <Text style={styles.errorStyle}>{error}</Text> : null}
    </Box>
  );
};

const styles = StyleSheet.create({
  inputStyle: {
    color: theme.colors["neutral.900"],
    borderColor: "transparent",
    paddingHorizontal: theme.spacing.m,
    flex: 1,
    ...theme.body["l5-medium"],
    lineHeight: normalize(20),
    marginTop: 0,
    outlineWidth: 0,
    "-webkit-focus-ring-color": "none",
    "-webkit-appearance": "none",
  },
  inputStyleWebDesktop: {
    color: theme.colors["neutral.900"],
    borderColor: "transparent",
    paddingHorizontal: theme.spacing.m,
    flex: 1,
    ...theme.body["l5-medium"],
    lineHeight: normalize(20),
    marginTop: 0,
    outlineWidth: 0,
    "-webkit-focus-ring-color": "none",
    "-webkit-appearance": "none",
  },
  errorStyle: {
    marginLeft: 15,
    marginRight: 15,
    fontSize: hp("2%"),
    color: "red",
    marginTop: -10,
  },
  containerStyle: {
    color: theme.colors["neutral.white"],
    backgroundColor: theme.colors["neutral.100"],
    flexDirection: "row",
    width: "100%",
    borderWidth: 0,
    height: normalize(60),
    borderRadius: normalize(8),
    alignItems: "center",
    justifyContent: "flex-start",
  },
  icon: {
    color: theme.colors["neutral.white"],
    marginRight: normalize(15),
    marginLeft: normalize(10),
  },
});

export { RoundInput };
